import "@mui/lab/themeAugmentation";
import { createTheme } from "@mui/material";
import {
  palette,
  flatAppColors,
} from "./colors";
import { createMuiTypography, radius } from "./designTokens";

/**
 * Uygulama genelinde kullanılan tek kaynak renk paleti.
 * Inline hex yerine `appColors`, `palette` veya `theme.palette.colors` kullanın.
 */
export { palette, colors, appColorTokens, primaryAlpha } from "./colors";
export { typography as designTypography, spacing, radius, layout, layoutTokens } from "./designTokens";
export const appColors = flatAppColors;

/** Mobil `palette.border.main` ile aynı — form/kart yüzeyleri */
export const formSurfaceBorder = `1px solid ${palette.border.main}`;

const theme = createTheme({
  typography: createMuiTypography(),
  shape: {
    borderRadius: radius.md,
  },
  components: {
    MuiInputLabel: {
      defaultProps: {
        style: {
          fontWeight: "bold",
          backgroundColor: "white",
          paddingLeft: 0.25,
          paddingRight: 0.25,
        },
      },
      styleOverrides: {
        root: {
          color: "rgba(0, 0, 0, 0.6) !important",
          "&.Mui-focused": {
            color: `${appColors.primary} !important`,
          },
          "&.MuiInputLabel-shrink": {
            color: "rgba(0, 0, 0, 0.6) !important",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23) !important",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.87) !important",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${appColors.primary} !important`,
          },
          "& .MuiInputBase-input::placeholder": {
            color: `${appColors.gray500} !important`,
            opacity: "1 !important",
          },
          "& .MuiInputBase-input": {
            "&::placeholder": {
              color: `${appColors.gray500} !important`,
              opacity: "1 !important",
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            color: "rgba(0, 0, 0, 0.6) !important",
            "&.Mui-focused": {
              color: `${appColors.primary} !important`,
            },
            "&.MuiInputLabel-shrink": {
              color: "rgba(0, 0, 0, 0.6) !important",
            },
          },
          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23) !important",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.87) !important",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${appColors.primary} !important`,
            },
            "& .MuiInputBase-input::placeholder": {
              color: `${appColors.gray500} !important`,
              opacity: "1 !important",
            },
            "& .MuiInputBase-input": {
              "&::placeholder": {
                color: `${appColors.gray500} !important`,
                opacity: "1 !important",
              },
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        style: {
          fontWeight: "bold",
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        style: {
          fontWeight: "bold",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9375rem",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
          "&:active": { boxShadow: "none" },
        },
        containedPrimary: {
          backgroundColor: palette.button.default.main,
          color: palette.common.white,
          "&:hover": {
            backgroundColor: palette.button.default.dark,
            boxShadow: "none",
          },
        },
        containedSuccess: {
          backgroundColor: palette.button.success.main,
          color: palette.common.white,
          "&:hover": {
            backgroundColor: palette.button.success.dark,
            boxShadow: "none",
          },
        },
        containedError: {
          backgroundColor: palette.button.error.main,
          color: palette.common.white,
          "&:hover": {
            backgroundColor: palette.button.error.dark,
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: palette.border.light,
          color: palette.gray.text,
          "&:hover": {
            borderColor: palette.border.main,
            backgroundColor: palette.background.main,
            boxShadow: "none",
          },
        },
        text: {
          color: palette.brand.main,
          "&:hover": {
            backgroundColor: palette.brand[50],
          },
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          "&:hover": { boxShadow: "none" },
          "&:active": { boxShadow: "none" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": { boxShadow: "none" },
        },
      },
    },
    MuiFab: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          "&:hover": { boxShadow: "none" },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: formSurfaceBorder,
          borderRadius: radius.lg,
          backgroundImage: "none",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: formSurfaceBorder,
          borderRadius: radius.lg,
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: formSurfaceBorder,
          borderRadius: radius.lg,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: "none",
          boxShadow: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: "none",
        },
      },
    },
  },
  palette: {
    primary: {
      main: palette.brand.main,
      light: palette.brand.light,
      dark: palette.brand.dark,
      contrastText: palette.common.white,
    },
    background: {
      main: palette.background.app,
      secondary: palette.background.secondary,
    },
    success: {
      main: palette.success.main,
      secondary: palette.success.main,
      contrastText: palette.common.white,
    },
    error: {
      main: palette.error.red,
      light: palette.error.light,
      contrastText: palette.common.white,
    },
    warning: {
      main: palette.warning.orange,
      contrastText: palette.common.white,
    },
    info: {
      main: palette.accent.blue,
      contrastText: palette.common.white,
    },
    text: {
      primary: palette.text.main,
      disabled: palette.text.secondary,
      secondary: palette.text.secondary,
    },
    colors: {
      main: palette.brand.main,
      placeholder: palette.text.placeholder,
      lightGray: palette.border.gray,
      mediumGray: palette.gray.text,
      darkGray: palette.text.main,
      borderMain: palette.border.main,
      borderLight: palette.border.light,
      ...flatAppColors,
    },
  },
});

export default theme;
